import { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { PollingProvider } from './contexts/PollingContext';
import { NavDropdown } from './components/layout/NavDropdown';
import { ContextSwitcher } from './components/ContextSwitcher';
import { User, LogOut } from 'lucide-react';
import { APP_VERSION } from './version';
import { BRAND, LOGO, ICON } from './lib/url';

function App() {
  const { user, logout, isAuthenticated, isSuperAdmin, isAdminClient } = useAuth();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate('/login');
  };

  return (
    <PollingProvider>
      <div className="h-screen flex flex-col bg-gray-100">
        <header className="bg-white shadow sticky top-0 z-50 flex-shrink-0">
        <div className="max-w-7xl mx-auto py-6 px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Logo complet (large) sur ≥ md ; icône carrée (ICON) sur écran
                  étroit pour éviter l'écrasement horizontal. Les deux suivent le
                  branding (VITE_APP_LOGO / VITE_APP_ICON). */}
              <img
                src={LOGO}
                alt={BRAND}
                className="hidden md:block h-12 w-auto flex-shrink-0"
              />
              <img
                src={ICON}
                alt={BRAND}
                className="block md:hidden h-12 w-auto flex-shrink-0"
              />
              <span className="text-[10px] font-medium text-gray-400 bg-gray-100 rounded px-1.5 py-0.5 self-end mb-1">
                v{APP_VERSION}
              </span>
            </div>

            {isAuthenticated && user && (
              <div className="flex items-center gap-4">
                <nav className="flex items-center gap-2">
                  <NavDropdown
                    label="Extraction"
                    items={[
                      { label: 'Document', path: '/extraction/documents' },
                      // PROD1.4.7 #501 — Analytics réservé admin_client / superadmin
                      // (cohérence avec ProtectedRoute requireAdminClient).
                      ...(isAdminClient() ? [
                        { label: 'Analytics', path: '/extraction/analytics' },
                      ] : []),
                    ]}
                    isOpen={openDropdown === 'extraction'}
                    onOpen={() => setOpenDropdown('extraction')}
                    onClose={() => setOpenDropdown(prev => prev === 'extraction' ? null : prev)}
                  />
                  {(isSuperAdmin() || isAdminClient()) && (
                    <Link
                      to="/mon-tenant"
                      className="px-4 py-2 rounded text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Mon tenant
                    </Link>
                  )}
                  {isSuperAdmin() && (
                    <>
                      <NavDropdown
                        label="Benchmark"
                        items={[
                          { label: 'Analyse', path: '/benchmark/analyse' },
                          { label: 'Comparaison', path: '/benchmark/comparaison' },
                          { label: 'Documents sources', path: '/benchmark/ground-truth' },
                          { label: 'Optimisation', path: '/benchmark/optimisation' },
                        ]}
                        isOpen={openDropdown === 'benchmark'}
                        onOpen={() => setOpenDropdown('benchmark')}
                        onClose={() => setOpenDropdown(prev => prev === 'benchmark' ? null : prev)}
                      />
                      <NavDropdown
                        label="Paramètres"
                        items={[
                          { label: 'Clients', path: '/parametres/clients' },
                          { label: 'Domaines', path: '/parametres/domaines' },
                          { label: 'Prompts', path: '/parametres/prompts' },
                          { label: 'Schémas', path: '/parametres/schemas' },
                          { label: 'OCR', path: '/parametres/ocr' },
                          { label: 'LLM', path: '/parametres/llm' },
                          { label: 'Modèles LLM', path: '/parametres/llm-models' },
                          ...(isSuperAdmin() ? [
                            { label: 'Utilisateurs', path: '/parametres/utilisateurs' },
                            { label: 'Jobs RQ', path: '/parametres/jobs' },
                            { label: 'Statistiques', path: '/parametres/statistiques' },
                            { label: 'Intégrations Mirador', path: '/parametres/integrations/mirador' },
                          ] : []),
                        ]}
                        isOpen={openDropdown === 'parametres'}
                        onOpen={() => setOpenDropdown('parametres')}
                        onClose={() => setOpenDropdown(prev => prev === 'parametres' ? null : prev)}
                      />
                    </>
                  )}
                </nav>
                <ContextSwitcher />
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <User size={16} />
                    <span>{user.full_name || user.email}</span>
                    {isSuperAdmin() && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">Superadmin</span>
                    )}
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[180px] py-1 z-50">
                      <button
                        onClick={() => { setUserMenuOpen(false); navigate('/compte'); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <User size={14} />
                        Mon compte
                      </button>
                      <hr className="my-1 border-gray-200" />
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <LogOut size={14} />
                        Déconnexion
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

        <main className="flex-1 overflow-auto" style={{ scrollbarGutter: 'stable' }}>
            <Outlet />
        </main>
      </div>
    </PollingProvider>
  );
}

export default App;
